'use client';

import { c as _c } from "react/compiler-runtime";
import { usePrefetchGetMarkets, usePrefetchGetProposals, usePrefetchGetWinners } from '@morfi/backend-sdk-v2/client';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';
import { NavigationList, NavigationListItem } from '@/components/ui/navigation-list';
import { categoryNavTargetBase, categorySwitchesInPlace, isCategoryFilterListingSurface } from '@/config/navigation';
import { categoryNameToId, DISCOVERY_CATEGORIES } from '@/config/proposal-options';
import { prefetchRouteFull } from '@/hooks/use-intent-route-prefetch';
import { usePathname } from '@/i18n/navigation';
import { useAppRouter } from '@/lib/network/use-app-router';
import { isAttributionParam, pushOwnedSearchParams } from '@/lib/url/search-params';
import { warmArgsForBase } from './feed-intent-prefetch';

// Category is sent as the canonical slug (`categoryNameToId`) because every
// feed converts the URL name → slug at the wire boundary; sending the raw
// display name would key a different (missed) entry. The per-surface base args
// live in `./feed-intent-prefetch` (shared with the header tab nav) so the
// byte-match payloads never drift between the two hover-prefetch surfaces.
const VALID_CATEGORIES: ReadonlySet<string> = new Set(DISCOVERY_CATEGORIES.map(c => c.name));
const CATEGORY_KEYS: ReadonlySet<string> = new Set(['category']);
export function CategoryNav() {
  const $ = _c(33);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const tNav = useTranslations("nav");
  const tCat = useTranslations("categories");
  const tHeader = useTranslations("header");
  const prefetchMarkets = usePrefetchGetMarkets();
  const prefetchWinners = usePrefetchGetWinners();
  const prefetchProposals = usePrefetchGetProposals();
  const base = categoryNavTargetBase(pathname);
  let t0;
  if ($[0] !== pathname) {
    t0 = isCategoryFilterListingSurface(pathname);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isListingSurface = t0;
  const inPlace = categorySwitchesInPlace(pathname);
  let t1;
  if ($[2] !== isListingSurface || $[3] !== searchParams) {
    const categoryParam = searchParams.get("category");
    t1 = isListingSurface && categoryParam !== null && VALID_CATEGORIES.has(categoryParam) ? categoryParam : null;
    $[2] = isListingSurface;
    $[3] = searchParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const activeCategory = t1;
  const prefetchFor = base === "/winners" ? prefetchWinners : base === "/proposals" ? prefetchProposals : prefetchMarkets;
  const warmBase = warmArgsForBase(base);
  const router = useAppRouter();
  const prefetchAll = function prefetchAll() {
    if (!isListingSurface) {
      return;
    }
    if (!inPlace) {
      prefetchRouteFull(router, base);
    }
    prefetchFor(warmBase);
  };
  const T0 = NavigationList;
  let t2;
  if ($[5] !== tHeader) {
    t2 = tHeader("categoryNavigation");
    $[5] = tHeader;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = "from-background-secondary";
  const t4 = "none";
  const t5 = "pl-4 sm:pl-6 lg:pl-0";
  const T1 = NavigationListItem;
  const t6 = categoryHref({
    base,
    inPlace,
    searchParams,
    category: null
  });
  const t7 = isListingSurface && activeCategory === null;
  let t8;
  if ($[7] !== inPlace) {
    t8 = event => handleCategoryClick(event, inPlace, null);
    $[7] = inPlace;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== tNav) {
    t9 = tNav("allCategories");
    $[9] = tNav;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== T1 || $[12] !== prefetchAll || $[13] !== t6 || $[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
    t10 = <T1 href={t6} active={t7} prefetch={false} onMouseEnter={prefetchAll} onFocus={prefetchAll} onPointerDown={prefetchAll} onClick={t8}>{t9}</T1>;
    $[11] = T1;
    $[12] = prefetchAll;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== activeCategory || $[19] !== base || $[20] !== inPlace || $[21] !== isListingSurface || $[22] !== prefetchFor || $[23] !== router || $[24] !== searchParams || $[25] !== tCat || $[26] !== warmBase) {
    t11 = DISCOVERY_CATEGORIES.map(cat => {
      const href = categoryHref({
        base,
        inPlace,
        searchParams,
        category: cat.name
      });
      const prefetchCategory = function prefetchCategory() {
        if (!isListingSurface) {
          return;
        }
        if (!inPlace) {
          prefetchRouteFull(router, href);
        }
        prefetchFor({
          ...warmBase,
          category: categoryNameToId(cat.name)
        });
      };
      return <NavigationListItem key={cat.id} href={href} active={activeCategory === cat.name} prefetch={false} onMouseEnter={prefetchCategory} onFocus={prefetchCategory} onPointerDown={prefetchCategory} onClick={event_0 => handleCategoryClick(event_0, inPlace, cat.name)}>{tCat(cat.id as keyof IntlMessages['categories'])}</NavigationListItem>;
    });
    $[18] = activeCategory;
    $[19] = base;
    $[20] = inPlace;
    $[21] = isListingSurface;
    $[22] = prefetchFor;
    $[23] = router;
    $[24] = searchParams;
    $[25] = tCat;
    $[26] = warmBase;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== T0 || $[29] !== t10 || $[30] !== t11 || $[31] !== t2) {
    t12 = <T0 aria-label={t2} gradientFrom={t3} edgeEffect={t4} scrollClassName={t5}>{t10}{t11}</T0>;
    $[28] = T0;
    $[29] = t10;
    $[30] = t11;
    $[31] = t2;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}

// On an in-place surface the link keeps the current params, so a new tab or a
// copied link shows the view a click produces, minus the ad params the visit
// landed with. Elsewhere the current page's params mean nothing on the feed.
function categoryHref({
  base,
  inPlace,
  searchParams,
  category
}: {
  base: string;
  inPlace: boolean;
  searchParams: URLSearchParams;
  category: string | null;
}): string {
  const params = new URLSearchParams();
  if (inPlace) {
    for (const [key, value] of searchParams) {
      if (!isAttributionParam(key)) params.append(key, value);
    }
  }
  if (category === null) params.delete('category');else params.set('category', category);
  const query = params.toString();
  return query.length > 0 ? `${base}?${query}` : base;
}

// On a feed that switches in place, a plain left click changes only
// `?category=`. Other pages and modified clicks keep the link's own behaviour:
// navigation, new tab, new window, download.
function handleCategoryClick(event: MouseEvent<HTMLAnchorElement>, inPlace: boolean, category: string | null): void {
  if (!inPlace || !isPlainLeftClick(event)) return;
  event.preventDefault();
  // The active pill changes nothing, so the feed keeps its scroll position.
  if (new URLSearchParams(window.location.search).get('category') === category) return;
  const params = new URLSearchParams();
  if (category !== null) params.set('category', category);
  pushOwnedSearchParams({
    pathname: window.location.pathname,
    params,
    ownedKeys: CATEGORY_KEYS
  });

  // Like the navigation it replaces: back to the top only once the feed's top
  // has scrolled out of view.
  if (isFeedTopOutOfView()) window.scrollTo({
    top: 0
  });
}
function isFeedTopOutOfView(): boolean {
  const feed = document.querySelector('main');
  return feed !== null && feed.getBoundingClientRect().top < 0;
}
function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}